export type DeerState = 'idle' | 'walk' | 'graze' | 'alert' | 'flee';
export interface DeerPlayer { x: number; z: number; speed: number; visible?: boolean }
export interface DeerObstacle { x: number; z: number; radius: number }
export interface DeerHabitat { radius: number; obstacles: readonly DeerObstacle[] }
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));
const angle = (v: number) => Math.atan2(Math.sin(v), Math.cos(v));

/** Small, deterministic ground-animal brain. Renderers supply the player sense
 * and habitat; this module owns decisions, heading and planar movement only. */
export class DeerBehavior {
  x = 0; z = -3; yaw = 0; speed = 0;
  state: DeerState = 'idle'; elapsed = 0; fear = 0; distance = Infinity;
  target = { x: 0, z: -3 }; lookAtPlayer = false;
  transitions: { from: DeerState; to: DeerState; time: number }[] = [];
  time = 0;
  private calmFor = 0;
  private dwell = 2.6;
  private roam = 0;
  private seed: number;
  private forced: DeerState | null = null;
  private turnTarget: number | null = null;
  turnRate = 0;

  constructor(readonly habitat: DeerHabitat, seed = 7541, readonly scale = 1.15) { this.seed = seed; }
  startle(): void { this.turnTarget = null; this.forced = null; this.fear = 1; this.enter('flee'); }
  notice(): void { this.turnTarget = null; this.forced = null; this.enter('alert'); this.elapsed = 0; }
  turnBy(radians: number): void { this.turnTarget = this.yaw + radians; this.forced = 'idle'; this.enter('idle'); }
  private random(): number { this.seed = (Math.imul(this.seed, 1664525) + 1013904223) >>> 0; return this.seed / 4294967296; }
  setMode(mode: DeerState | 'auto'): void {
    this.turnTarget = null;
    this.forced = mode === 'auto' ? null : mode;
    if (this.forced) this.enter(this.forced);
  }
  reset(x = 0, z = -3): void {
    this.x = x; this.z = z; this.yaw = 0; this.speed = 0; this.fear = 0;
    this.state = 'idle'; this.elapsed = 0; this.time = 0; this.calmFor = 0;
    this.dwell = 2.6; this.roam = 0; this.forced = null; this.transitions = [];
    this.target = { x, z }; this.lookAtPlayer = false;
    this.turnTarget = null; this.turnRate = 0;
  }
  private enter(state: DeerState): void {
    if (this.state === state) return;
    this.transitions.push({ from: this.state, to: state, time: this.time });
    if (this.transitions.length > 40) this.transitions.shift();
    this.state = state; this.elapsed = 0; this.calmFor = 0;
    this.dwell = state === 'graze' ? 6 : state === 'idle' ? 2 + this.random() * 2 : 8;
    if (state === 'walk') this.chooseTarget();
  }
  private clear(x: number, z: number, margin = .8): boolean {
    return Math.hypot(x, z) < this.habitat.radius - margin &&
      this.habitat.obstacles.every(o => Math.hypot(x - o.x, z - o.z) > o.radius + margin);
  }
  private chooseTarget(): void {
    for (let i = 0; i < 30; i++) {
      const a = this.random() * Math.PI * 2, r = 1.6 + this.random() * 2.4;
      const x = this.x + Math.sin(a) * r, z = this.z + Math.cos(a) * r;
      if (this.clear(x, z, 1.2)) { this.target = { x, z }; return; }
    }
    this.target = { x: 0, z: 0 };
  }
  update(dt: number, player: DeerPlayer): void {
    const previousYaw = this.yaw;
    dt = clamp(dt, 0, .1); this.time += dt; this.elapsed += dt;
    const dx = player.x - this.x, dz = player.z - this.z;
    this.distance = Math.hypot(dx, dz);
    const sensed = player.visible !== false;
    const danger = sensed && (this.distance < 2.8 || (this.distance < 6.5 && player.speed > 3.1));
    const noticed = sensed && this.distance < 7.5;
    this.fear = clamp(this.fear + dt * (danger ? 1.25 : noticed ? .02 : -.15), 0, 1);
    if (!this.forced) {
      if (danger && this.state !== 'flee') this.enter('flee');
      else if (this.state === 'flee') {
        this.calmFor = !danger && this.distance > 10 ? this.calmFor + dt : 0;
        if (this.elapsed > 2.8 && this.calmFor > 2) this.enter('alert');
      } else if (noticed) {
        if (this.state !== 'alert') this.enter('alert');
        this.calmFor = 0;
      } else if (this.state === 'alert') {
        this.calmFor += dt;
        if (this.calmFor > 3.5 && this.fear < .35) this.enter('idle');
      } else if (this.state === 'idle' && this.elapsed > this.dwell) this.enter(this.roam++ % 2 ? 'walk' : 'graze');
      else if (this.state === 'graze' && this.elapsed >= 6) this.enter('walk');
      else if (this.state === 'walk' && (Math.hypot(this.target.x - this.x, this.target.z - this.z) < .24 || this.elapsed > 12)) this.enter('idle');
    }
    this.lookAtPlayer = this.turnTarget === null && sensed && (this.state === 'alert' || (noticed && this.state !== 'graze' && this.state !== 'flee'));
    let vx = 0, vz = 0, desiredSpeed = 0;
    if (this.state === 'walk') {
      if (Math.hypot(this.target.x - this.x, this.target.z - this.z) < .24 && this.forced) this.chooseTarget();
      vx = this.target.x - this.x; vz = this.target.z - this.z; desiredSpeed = .37109375 * this.scale;
    } else if (this.state === 'flee') {
      vx = -dx; vz = -dz;
      if (Math.hypot(vx, vz) < .1) { vx = Math.sin(this.yaw + 1); vz = Math.cos(this.yaw + 1); }
      desiredSpeed = 3.7;
    } else if (this.turnTarget !== null) {
      const remaining = angle(this.turnTarget - this.yaw);
      this.yaw += clamp(remaining, -.65 * dt, .65 * dt);
      if (Math.abs(remaining) < .002) { this.turnTarget = null; this.forced = null; this.elapsed = 0; }
    } else if (this.lookAtPlayer && Math.abs(angle(Math.atan2(dx, dz) - this.yaw)) > .85) {
      // Turn the body slowly when the player leaves the neck's comfortable range.
      this.yaw += clamp(angle(Math.atan2(dx, dz) - this.yaw), -.45 * dt, .45 * dt);
    }
    const len = Math.hypot(vx, vz);
    if (len > 1e-6) {
      vx /= len; vz /= len;
      // Anticipatory steering includes the clearing boundary and tree/rock trunks.
      for (const o of this.habitat.obstacles) {
        const ox = this.x + vx * 1.8 - o.x, oz = this.z + vz * 1.8 - o.z, d = Math.hypot(ox, oz);
        if (d < o.radius + 1.5) { vx += ox / Math.max(.01, d) * 2; vz += oz / Math.max(.01, d) * 2; }
      }
      const edge = Math.hypot(this.x, this.z);
      if (edge > this.habitat.radius - 3) {
        const k = (edge - (this.habitat.radius - 3)) * 1.5;
        vx -= this.x / edge * k; vz -= this.z / edge * k;
      }
      const turn = angle(Math.atan2(vx, vz) - this.yaw);
      const limit = this.state === 'flee' ? 2.2 : 1.1;
      this.yaw += clamp(turn, -limit * dt, limit * dt);
      desiredSpeed *= Math.max(.12, Math.cos(turn));
    }
    this.speed += (desiredSpeed - this.speed) * (1 - Math.exp(-5 * dt));
    const px = this.x, pz = this.z;
    this.x += Math.sin(this.yaw) * this.speed * dt;
    this.z += Math.cos(this.yaw) * this.speed * dt;
    const edge = Math.hypot(this.x, this.z), bound = this.habitat.radius - .85;
    if (edge > bound) { this.x *= bound / edge; this.z *= bound / edge; }
    for (const o of [...this.habitat.obstacles, { x: player.x, z: player.z, radius: .33 }]) {
      const ox = this.x - o.x, oz = this.z - o.z, d = Math.hypot(ox, oz), r = o.radius + .48;
      if (d < r) { this.x = o.x + (d > .001 ? ox / d : 1) * r; this.z = o.z + (d > .001 ? oz / d : 0) * r; }
    }
    if (dt > 0 && desiredSpeed > 0) this.speed = Math.min(3.7, Math.hypot(this.x - px, this.z - pz) / dt);
    this.turnRate = dt > 0 ? angle(this.yaw - previousYaw) / dt : 0;
  }
}
